import { Link, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { Calendar, Clock, ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import TableOfContents from '../components/TableOfContents';
import useSEO from '../hooks/useSEO';
import { BLOG_POSTS } from '../data/blog';
import { getAuthorForPost } from '../data/authors';

function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = id;
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, [id, data]);
}

// Convert a heading into a stable, URL-safe id for the ToC anchors.
function slugify(str) {
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/blog/${slug}` : undefined;

  useSEO({
    title: post ? `${post.title} — Ransen` : undefined,
    description: post?.excerpt,
    canonical,
  });

  const articleSchema = post ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    keywords: (post.tags || []).join(', '),
    author: (() => {
      const a = getAuthorForPost(post.slug);
      return {
        '@type': 'Person',
        name: a.name,
        url: `https://ransen.io/authors/${a.slug}`,
        jobTitle: a.title,
        image: a.avatar,
        sameAs: [a.linkedin, a.twitter].filter(Boolean),
      };
    })(),
    publisher: {
      '@type': 'Organization',
      name: 'Ransen',
      logo: { '@type': 'ImageObject', url: typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : undefined },
    },
    mainEntityOfPage: canonical,
  } : null;

  useJsonLd('blog-article-schema', articleSchema);

  // Reset scroll on slug change so the reader lands at the top of a new post
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  // Build the ToC descriptor list ({ id, label }) — must be a hook, so runs before any early return
  const sections = post?.sections || [
    ...(post?.heading1 ? [{ heading: post.heading1, paragraphs: post.body || [] }] : []),
    ...(post?.heading2 ? [{ heading: post.heading2, paragraphs: post.body2 || [] }] : []),
  ];
  const toc = useMemo(
    () =>
      sections.map((s, i) => ({
        id: `sec-${i + 1}-${slugify(s.heading)}`,
        // Strip a leading "N." or "N)" that already exists in the heading so the ToC number isn't duplicated.
        label: String(s.heading).replace(/^\s*\d+\s*[.)]\s*/, ''),
      })),
    [sections]
  );

  if (!post) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>Post not found</h1>
        <Link to='/blog' className='mt-6 inline-block text-brand' data-testid='blog-back-to-index'>Back to blog</Link>
      </div>
    );
  }

  const intro = post.intro || post.body || [];
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const author = getAuthorForPost(post.slug);

  return (
    <div data-testid='blog-post-page'>
      <PageHeader
        eyebrow={post.category}
        title={<span className='text-white'>{post.title}</span>}
        description={post.excerpt}
        crumbs={[{ label: 'Blog', to: '/blog' }, { label: post.title.length > 40 ? post.title.slice(0, 40) + '…' : post.title }]}
      />

      <section className='py-10'>
        <div className='max-w-7xl mx-auto px-6'>
          {/* Meta row (dark theme, above the paper) */}
          <div className='flex flex-wrap gap-4 text-white/60 text-[13px] mb-6 lg:pl-[300px]'>
            <span className='inline-flex items-center gap-1.5'><Calendar className='w-3.5 h-3.5' /> {post.date}</span>
            <span className='inline-flex items-center gap-1.5'><Clock className='w-3.5 h-3.5' /> {post.read}</span>
            <Link
              to={`/authors/${author.slug}`}
              className='inline-flex items-center gap-1.5 hover:text-white transition-colors'
              data-testid='blog-author-link'
            >
              <BookOpen className='w-3.5 h-3.5' /> {author.name}
            </Link>
          </div>

          {/* 2-column layout on lg+: ToC | Article */}
          <div className='grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-10'>
            {/* Table of contents — one instance, internal responsive rendering */}
            <div>
              <TableOfContents sections={toc} />
            </div>

            <div>
              {/* White paper reading container */}
              <article
                data-testid='blog-post-article'
                className='bg-white text-slate-900 rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-black/5 overflow-hidden'
              >
                <div className='overflow-hidden'>
                  <img src={post.image} alt={post.title} className='w-full h-auto' />
                </div>

                <div className='px-6 py-10 md:px-12 md:py-14'>
                  <h1 className='font-heading text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-6'>
                    {post.title}
                  </h1>

                  {/* Author byline — E-E-A-T signal + LLMO citation attribution */}
                  <Link
                    to={`/authors/${author.slug}`}
                    className='flex items-center gap-4 mb-8 pb-6 border-b border-slate-100 group'
                    data-testid='blog-author-byline'
                  >
                    <img src={author.avatar} alt={author.name} className='w-12 h-12 rounded-full object-cover ring-2 ring-brand/20' />
                    <div>
                      <div className='font-heading text-[15.5px] font-semibold text-slate-900 group-hover:text-brand transition-colors'>
                        {author.name}
                      </div>
                      <div className='text-[12.5px] text-slate-500'>
                        {author.title} · {author.experienceYears}+ years experience
                      </div>
                    </div>
                  </Link>

                  {intro.length > 0 && (
                    <div className='mb-10 border-l-4 border-brand/60 pl-5'>
                      {intro.map((p, i) => (
                        <p key={i} className='text-slate-700 text-[17px] leading-[1.75] font-body mb-4 last:mb-0'>
                          {p}
                        </p>
                      ))}
                    </div>
                  )}

                  {sections.map((sec, sIdx) => (
                    <section key={sIdx} id={toc[sIdx]?.id} className='mb-10 last:mb-0 scroll-mt-28'>
                      <h2 className='font-heading text-2xl md:text-[28px] font-bold text-slate-900 mt-2 mb-5 leading-snug'>
                        {sec.heading}
                      </h2>
                      {(sec.paragraphs || []).map((p, i) => (
                        <p key={i} className='text-slate-700 text-[16.5px] leading-[1.8] font-body mb-4'>
                          {p}
                        </p>
                      ))}
                      {sec.list && sec.list.length > 0 && (
                        <ul className='mt-4 space-y-4'>
                          {sec.list.map((item, iIdx) => (
                            <li key={iIdx} className='pl-5 border-l-2 border-slate-200'>
                              <div className='font-heading text-[16px] font-semibold text-slate-900 mb-1'>{item.title}</div>
                              <div className='text-slate-600 text-[15.5px] leading-[1.75] font-body'>{item.text}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  ))}

                  <div className='mt-12 pt-8 border-t border-slate-200 flex flex-wrap gap-2'>
                    {(post.tags || []).map((t) => (
                      <span key={t} className='px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-[12px] text-slate-700'>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </article>

              {/* Post-article actions */}
              <div className='mt-10 flex items-center justify-between'>
                <Link to='/blog' className='inline-flex items-center gap-2 text-brand hover:text-white text-sm font-medium' data-testid='blog-back-link'>
                  <ArrowLeft className='w-4 h-4' /> All articles
                </Link>
                <Link to='/contact' className='btn-primary text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2' data-testid='blog-cta-strategy-call'>
                  Book a Strategy Call <ArrowRight className='w-4 h-4' />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className='py-16 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <h3 className='font-heading text-2xl md:text-3xl font-bold text-white mb-8'>Continue reading</h3>
            <div className='grid md:grid-cols-3 gap-6'>
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className='glass-card rounded-2xl overflow-hidden group' data-testid={`related-post-${r.slug}`}>
                  <div className='relative aspect-[16/9] overflow-hidden'>
                    <img src={r.image} alt={r.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                    <div className='absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[10.5px] tracking-wider uppercase font-accent'>{r.category}</div>
                  </div>
                  <div className='p-5'>
                    <h4 className='font-heading text-[15px] font-bold text-white leading-snug group-hover:text-brand transition-colors'>{r.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
